import { useReducer } from 'react';

import { HttpStatusCode, PejHttpError } from '@common-lib';

import { useRoot } from '@frontend/utils';

type ActionState<T> =
  | { loading: boolean; data?: undefined; error?: undefined }
  | { loading: false; data: T; error?: undefined }
  | { loading: false; error: Error; data?: undefined };

type Action<T> =
  | { type: 'busy' }
  | { type: 'success'; result: T }
  | { type: 'error'; error: Error };

const actionReducer = <T>(
  state: ActionState<T>,
  action: Action<T>,
): ActionState<T> => {
  switch (action.type) {
    case 'busy':
      return { loading: true, error: undefined, data: undefined };
    case 'success':
      return { loading: false, data: action.result, error: undefined };
    case 'error':
      return {
        loading: false,
        error: action.error,
        data: undefined,
      };
    default:
      return state;
  }
};

export interface UseSubmitProps<TResult, TParams> {
  onSubmit: (params: TParams) => Promise<TResult>;
  onError?: (e: Error) => void;
  onFinally?: () => void;
}

export interface UseSubmitResponse<TResponse, TParams> {
  submit: (params: TParams) => Promise<TResponse | void>;
  submitting: boolean;
  error: Error | undefined;
  response?: TResponse;
}

export const useSubmit = <T, TParams>({
  onSubmit,
  onError,
  onFinally,
}: UseSubmitProps<T, TParams>): UseSubmitResponse<T, TParams> => {
  const { removeLocalSession } = useRoot();
  const [state, dispatch] = useReducer<ActionState<T>, [Action<T>]>(
    actionReducer,
    {
      loading: false,
    },
  );

  const submit = (params: TParams): Promise<T | void> => {
    dispatch({ type: 'busy' });

    return onSubmit(params)
      .then((result) => {
        dispatch({ type: 'success', result });
        return result;
      })
      .catch((e) => {
        if (e instanceof PejHttpError) {
          if (
            e.statusCode === HttpStatusCode.UNAUTHORIZED ||
            e.statusCode === HttpStatusCode.LOCKED
          ) {
            removeLocalSession();
            return;
          }
        }

        if (e instanceof Error) {
          dispatch({ type: 'error', error: e });
        }

        onError?.(e);

        // The form wrapper needs to know that the submit failed
        throw e;
      })
      .finally(() => {
        onFinally?.();
      });
  };

  return {
    submit,
    submitting: state.loading,
    error: state.error,
    response: state.data as T,
  };
};
